import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentFinancialYear, inr, type TaxDeclarationItem, type TaxProjection } from '@hrms/shared';
import { api } from '../../../lib/api';
import { useMut } from '../../../lib/query';
import { useGo } from '../../../navigation/nav';
import { Alert, Badge, Button, Card, Checkbox, Columns, DescList, ErrorState, IconButton, Loading, PageHeader, RadioGroup, Row, Screen, Select, Stack, StatusBadge, Text, TextField, useTheme } from '../../../ui';
import { fyOptions, TAX_SECTIONS } from './tax';

function ProjectionCard({ p, title, recommended }: { p: TaxProjection | undefined; title: string; recommended?: boolean }) {
  return (
    <Card title={title} actions={recommended ? <Badge tone="success">Recommended</Badge> : undefined}>
      {!p ? (
        <Loading />
      ) : (
        <DescList
          items={[
            ['Gross annual', inr(p.gross_annual)],
            ['Exemptions', inr(p.exemptions)],
            ['Standard deduction', inr(p.standard_deduction)],
            ['Chapter VI-A deductions', inr(p.deductions)],
            ['Taxable income', inr(p.taxable_income)],
            ['Tax + cess', inr(p.total_tax)],
            ['TDS so far', inr(p.tds_deducted)],
            ['Monthly TDS ahead', inr(p.monthly_tds)],
          ]}
        />
      )}
    </Card>
  );
}

const num = (s: string) => Number(s.replace(/[^0-9.]/g, '')) || 0;

export function TaxDeclarationScreen() {
  const go = useGo();
  const { colors } = useTheme();
  const [fy, setFy] = useState(currentFinancialYear());
  const q = useQuery({ queryKey: ['tax', 'declaration', fy], queryFn: () => api.tax.myDeclaration(fy) });
  const oldP = useQuery({ queryKey: ['tax', 'projection', 'old'], queryFn: () => api.tax.projection(undefined, 'old') });
  const newP = useQuery({ queryKey: ['tax', 'projection', 'new'], queryFn: () => api.tax.projection(undefined, 'new') });
  const [regime, setRegime] = useState<'old' | 'new'>('new');
  const [items, setItems] = useState<TaxDeclarationItem[]>([]);
  useEffect(() => {
    if (q.data) {
      setRegime(q.data.regime);
      setItems(q.data.items ?? []);
    }
  }, [q.data]);
  const save = useMut((submit: boolean) => api.tax.saveMyDeclaration({ financial_year: fy, regime, items, submit }), {
    invalidate: [['tax']],
    success: (_r, submit) => (submit ? 'Declaration submitted' : 'Draft saved'),
  });
  const locked = q.data?.status === 'locked';
  const upd = (i: number, patch: Partial<TaxDeclarationItem>) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const rec = oldP.data?.comparison?.recommended ?? newP.data?.comparison?.recommended ?? (oldP.data && newP.data ? (oldP.data.total_tax < newP.data.total_tax ? 'old' : 'new') : undefined);
  const totals = TAX_SECTIONS.map((s) => ({ s, amt: items.filter((i) => i.section === s.key).reduce((a, i) => a + (i.declared_amount || 0), 0) })).filter((x) => x.amt > 0);

  if (q.isLoading) return <Loading />;
  if (q.isError) return <Screen><PageHeader title="Tax declaration" /><ErrorState error={q.error} onRetry={() => q.refetch()} /></Screen>;
  return (
    <Screen>
      <PageHeader
        title="Tax declaration"
        subtitle="Declare investments for TDS computation. Proofs are collected later in the year."
        actions={
          <>
            <Select value={fy} onChange={setFy} options={fyOptions()} />
            <Button title="Save draft" disabled={locked} loading={save.isPending && save.variables === false} onPress={() => save.mutate(false)} />
            <Button title="Submit" variant="primary" disabled={locked} loading={save.isPending && save.variables === true} onPress={() => save.mutate(true)} />
            <Button title="Upload proofs" variant="ghost" icon="upload" onPress={() => go('TaxProofs')} />
          </>
        }
      />
      <Columns>
        <Stack>
          {locked && <Alert tone="info">This declaration is locked by payroll.</Alert>}
          <Card title="Tax regime" actions={q.data ? <StatusBadge status={q.data.status} /> : undefined}>
            <RadioGroup
              horizontal
              disabled={locked}
              value={regime}
              onChange={setRegime}
              options={[
                { value: 'new', label: 'New regime (115BAC)', description: rec === 'new' ? 'Saves more' : undefined },
                { value: 'old', label: 'Old regime', description: rec === 'old' ? 'Saves more' : undefined },
              ]}
            />
            {regime === 'new' && (
              <Text variant="small" style={{ marginTop: 8 }}>
                Most deductions (80C, HRA, etc.) do not apply under the new regime, except employer NPS.
              </Text>
            )}
          </Card>
          <Card title="Investments & deductions" actions={<Button size="sm" icon="plus" title="Add item" disabled={locked} onPress={() => setItems([...items, { section: '80C', description: '', declared_amount: 0 }])} />}>
            {items.length === 0 && <Text variant="muted">No items declared. Add your investments.</Text>}
            <Stack gap={12}>
              {items.map((it, i) => (
                <View key={i} style={{ gap: 8, paddingBottom: 12, borderBottomWidth: i < items.length - 1 ? 1 : 0, borderBottomColor: colors.border }}>
                  <Row wrap={false} align="flex-start">
                    <View style={{ flex: 1 }}>
                      <Select value={it.section} disabled={locked} onChange={(v) => upd(i, { section: v })} options={TAX_SECTIONS.map((s) => ({ value: s.key, label: s.label }))} />
                    </View>
                    <IconButton icon="trash" label="Remove item" onPress={() => !locked && setItems(items.filter((_, j) => j !== i))} />
                  </Row>
                  <Row wrap align="flex-start">
                    <TextField containerStyle={{ flex: 2, minWidth: 160 }} placeholder="Description" accessibilityLabel="Description" value={it.description} disabled={locked} onChangeText={(v) => upd(i, { description: v })} />
                    <TextField containerStyle={{ flex: 1, minWidth: 120 }} placeholder="₹ / year" accessibilityLabel="Annual amount" keyboardType="numeric" value={it.declared_amount ? String(it.declared_amount) : ''} disabled={locked || it.section === 'HRA'} onChangeText={(v) => upd(i, { declared_amount: num(v) })} />
                  </Row>
                  {it.section === 'HRA' && (
                    <Row>
                      <TextField containerStyle={{ width: 180 }} placeholder="Monthly rent" accessibilityLabel="Monthly rent" keyboardType="numeric" value={it.monthly_rent ? String(it.monthly_rent) : ''} disabled={locked} onChangeText={(v) => upd(i, { monthly_rent: num(v), declared_amount: num(v) * 12 })} />
                      <Checkbox label="Metro city (50%)" value={!!it.is_metro} disabled={locked} onChange={(v) => upd(i, { is_metro: v })} />
                    </Row>
                  )}
                  {it.approved_amount != null && <Text variant="small">Approved: {inr(it.approved_amount)}</Text>}
                </View>
              ))}
            </Stack>
          </Card>
          {totals.length > 0 && (
            <Card title="Summary by section">
              <Stack gap={8}>
                {totals.map(({ s, amt }) => (
                  <Row key={s.key} justify="space-between">
                    <Text>{s.key}</Text>
                    <Row gap={6}>
                      <Text weight="semibold">{inr(amt)}</Text>
                      {!!s.limit && amt > s.limit && <Badge tone="warning">{`capped at ${inr(s.limit)}`}</Badge>}
                    </Row>
                  </Row>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
        <Stack>
          <Card title="Old vs new regime">
            {oldP.data && newP.data ? (
              <Stack gap={8}>
                <Row justify="space-between"><Text>Old regime tax</Text><Text weight="semibold">{inr(oldP.data.total_tax)}</Text></Row>
                <Row justify="space-between"><Text>New regime tax</Text><Text weight="semibold">{inr(newP.data.total_tax)}</Text></Row>
                <Alert tone="success">{`${rec === 'old' ? 'Old' : 'New'} regime saves ${inr(Math.abs(oldP.data.total_tax - newP.data.total_tax))} this year.`}</Alert>
                <Text variant="small">Projection is based on your last saved declaration.</Text>
              </Stack>
            ) : oldP.isError || newP.isError ? (
              <Text variant="muted">Projection unavailable.</Text>
            ) : (
              <Loading />
            )}
          </Card>
          <ProjectionCard p={regime === 'old' ? oldP.data : newP.data} title={`Projection — ${regime} regime`} recommended={rec === regime} />
        </Stack>
      </Columns>
    </Screen>
  );
}
